import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AccessGate } from '../components/AccessGate';
import { ErrorSummary, ImportedHistoryBadge, Pager, StepHeading } from '../components/basics';
import { StatementList } from '../components/StatementList';
import { describeError, isNotFound } from '../describeError';
import { formatDate, formatDateRange, formatDateTime, formatMoney, sourceSystemLabel } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { payrollHistoryImportGateway } from '../index';
import { HISTORY_PATH, IMPORT_BASE, historyRunPath } from '../routes';
import '../payroll-history-import.css';

const PAGE_SIZE = 10;

function HistoryList() {
  const [page, setPage] = useState(1);
  const load = useCallback((signal: AbortSignal) => payrollHistoryImportGateway.listHistoricalRuns(page, PAGE_SIZE, signal), [page]);
  // Keep the previous page mounted while the next one loads so keyboard focus stays on the pager button.
  const { loading, data, error, reload } = useAsyncData(load, { keepPrevious: true });

  return (
    <>
      <StepHeading>Imported payroll history</StepHeading>
      <p className="phi-lead">
        Payroll records brought in from your previous payroll system. They are shown for reference only.
      </p>
      <p><Link to={IMPORT_BASE}>Back to import history</Link></p>

      {loading && !data && <p className="muted" role="status">Loading imported payroll history…</p>}
      {!loading && error !== undefined && (
        <div>
          <ErrorSummary message={describeError(error, 'Could not load the imported payroll history.')} />
          <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
        </div>
      )}
      {!loading && error === undefined && data && data.items.length === 0 && <p>No payroll history has been imported yet.</p>}
      {error === undefined && data && data.items.length > 0 && (
        <div aria-busy={loading}>
          <div className="table-scroll">
            <table className="phi-table-wide">
              <caption>Imported payroll runs</caption>
              <thead>
                <tr>
                  <th scope="col">Pay date</th>
                  <th scope="col">Pay period</th>
                  <th scope="col">Origin</th>
                  <th scope="col">Source platform</th>
                  <th scope="col">Type</th>
                  <th scope="col" className="num">Employees</th>
                  <th scope="col" className="num">Gross wages</th>
                  <th scope="col" className="num">Net pay</th>
                  <th scope="col">Imported</th>
                  <th scope="col"><span className="phi-sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((run) => (
                  <tr key={run.id}>
                    <th scope="row">{formatDate(run.payDate)}</th>
                    <td>{formatDateRange(run.periodStart, run.periodEnd)}</td>
                    <td><ImportedHistoryBadge /></td>
                    <td>{sourceSystemLabel(run.sourceSystem)}</td>
                    <td>{run.payrollType}{run.runStatus === 'Voided' ? ' (voided)' : ''}</td>
                    <td className="num">{run.employeeCount}</td>
                    <td className="num">{formatMoney(run.grossPay)}</td>
                    <td className="num">{formatMoney(run.netPay)}</td>
                    <td>{formatDateTime(run.importedAtUtc)}<div className="muted">{run.importedByEmail}</div></td>
                    <td>
                      <Link to={historyRunPath(run.id)} aria-label={`View details for the run paid ${formatDate(run.payDate)}`}>View details</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} pageSize={data.pageSize} totalCount={data.totalCount} onPage={setPage} label="Imported payroll history pages" />
        </div>
      )}
    </>
  );
}

function HistoryDetail({ runId }: { runId: string }) {
  const load = useCallback((signal: AbortSignal) => payrollHistoryImportGateway.getHistoricalRun(runId, signal), [runId]);
  const { loading, data, error, reload } = useAsyncData(load);

  if (loading) return <p className="muted" role="status">Loading imported payroll record…</p>;
  if (error !== undefined || !data) {
    if (isNotFound(error)) {
      return (
        <div className="phi-state">
          <StepHeading>Record not found</StepHeading>
          <p>We couldn&apos;t find that imported payroll record. It may not exist, or you may not have access to it.</p>
          <Link to={HISTORY_PATH}>View imported payroll history</Link>
        </div>
      );
    }
    return (
      <div>
        <StepHeading>Imported payroll record</StepHeading>
        <ErrorSummary message={describeError(error, 'Could not load this imported payroll record.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }

  const { run, statements } = data;
  const source = sourceSystemLabel(run.sourceSystem);
  return (
    <>
      <StepHeading>{`Imported payroll run: pay date ${formatDate(run.payDate)}`}</StepHeading>
      <p>
        <ImportedHistoryBadge />{' '}
        {run.runStatus === 'Voided' && (
          <span className="phi-badge phi-badge--blocked">
            <span aria-hidden="true">✕</span>
            Voided run
          </span>
        )}
      </p>
      <div className="phi-banner" role="note">
        <span className="phi-banner-icon" aria-hidden="true">i</span>
        <p>
          This record was imported from {source} and is shown for reference only. It was not calculated by Escochex Payroll Next and cannot be paid, deposited, filed, or reopened.
        </p>
      </div>
      <dl className="phi-summary" aria-label="Imported run totals">
        <div><dt>Pay period</dt><dd className="phi-summary-text">{formatDateRange(run.periodStart, run.periodEnd)}</dd></div>
        <div><dt>Pay date</dt><dd className="phi-summary-text">{formatDate(run.payDate)}</dd></div>
        <div><dt>Employees</dt><dd>{run.employeeCount}</dd></div>
        <div><dt>Gross wages</dt><dd>{formatMoney(run.grossPay)}</dd></div>
        <div><dt>Employee taxes</dt><dd>{formatMoney(run.employeeTaxes)}</dd></div>
        <div><dt>Deductions</dt><dd>{formatMoney(run.employeeDeductions)}</dd></div>
        <div><dt>Employer taxes</dt><dd>{formatMoney(run.employerTaxes)}</dd></div>
        <div><dt>Net pay</dt><dd>{formatMoney(run.netPay)}</dd></div>
      </dl>
      <dl className="phi-facts">
        <dt>Run status</dt><dd>{run.runStatus === 'Voided' ? 'Voided in the source system' : run.runStatus}</dd>
        <dt>Payroll type</dt><dd>{run.payrollType}</dd>
        <dt>Source platform</dt><dd>{source}</dd>
        <dt>Source run ID</dt><dd>{run.sourcePayrollRunId}</dd>
        <dt>Imported</dt><dd>{formatDateTime(run.importedAtUtc)} by {run.importedByEmail}</dd>
        <dt>Import ID</dt><dd>{run.batchId}</dd>
      </dl>
      <h3>Pay statements</h3>
      <StatementList statements={statements} defaultOpen />
      <p><Link to={HISTORY_PATH}>View imported payroll history</Link></p>
    </>
  );
}

export function HistoricalRunsPage() {
  return (
    <section className="card card--wide phi">
      <AccessGate require="read">{() => <HistoryList />}</AccessGate>
    </section>
  );
}

export function HistoricalRunDetailPage() {
  const { runId } = useParams();
  return (
    <section className="card card--wide phi">
      <AccessGate require="read">{() => (runId ? <HistoryDetail runId={runId} /> : null)}</AccessGate>
    </section>
  );
}
